import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  TagIcon,
  CalendarDaysIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  XMarkIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import DeleteConfirmationModal from './DeleteConfirmationModal';

interface Category {
  id: string;
  tournament_id: string;
  name: string;
  description: string | null;
  sort_order: number;
  created_at: string;
}

interface Round {
  id: string;
  tournament_id: string;
  round_number: number;
  name: string;
  date: string | null;
  notes: string | null;
  sort_order: number;
  created_at: string;
}

interface Props {
  tournamentId: string;
}

function CategoryForm({
  initial,
  onSave,
  onCancel,
}: {
  initial?: Partial<Category>;
  onSave: (data: { name: string; description: string }) => Promise<void>;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    await onSave({ name: name.trim(), description: description.trim() });
    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit} className="bg-gray-50 rounded-xl p-4 border border-gray-200 space-y-3">
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Category Name *</label>
        <input
          autoFocus
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="e.g. Gross, Net, Ladies, Senior"
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-golf-orange"
          required
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
        <input
          type="text"
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="Optional description"
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-golf-orange"
        />
      </div>
      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving || !name.trim()}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-golf-orange text-white rounded-lg hover:bg-golf-orange/90 disabled:opacity-50 transition-colors"
        >
          <CheckIcon className="w-4 h-4" />
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
}

function RoundForm({
  initial,
  nextRoundNumber,
  onSave,
  onCancel,
}: {
  initial?: Partial<Round>;
  nextRoundNumber: number;
  onSave: (data: { round_number: number; name: string; date: string; notes: string }) => Promise<void>;
  onCancel: () => void;
}) {
  const [roundNumber, setRoundNumber] = useState(initial?.round_number ?? nextRoundNumber);
  const [name, setName] = useState(initial?.name ?? `Round ${initial?.round_number ?? nextRoundNumber}`);
  const [date, setDate] = useState(initial?.date ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    await onSave({ round_number: roundNumber, name: name.trim(), date, notes: notes.trim() });
    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit} className="bg-gray-50 rounded-xl p-4 border border-gray-200 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Round Number *</label>
          <input
            type="number"
            min={1}
            value={roundNumber}
            onChange={e => {
              const n = parseInt(e.target.value);
              setRoundNumber(n);
              if (!initial?.name) setName(`Round ${n}`);
            }}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-golf-orange"
            required
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Round Name *</label>
          <input
            autoFocus
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. Round 1, Day 1"
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-golf-orange"
            required
          />
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Date</label>
        <input
          type="date"
          value={date}
          onChange={e => setDate(e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-golf-orange"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
        <input
          type="text"
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder="Optional notes"
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-golf-orange"
        />
      </div>
      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving || !name.trim()}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-golf-orange text-white rounded-lg hover:bg-golf-orange/90 disabled:opacity-50 transition-colors"
        >
          <CheckIcon className="w-4 h-4" />
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
}

export default function TournamentCategoriesRounds({ tournamentId }: Props) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [rounds, setRounds] = useState<Round[]>([]);
  const [disabledRounds, setDisabledRounds] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);

  const [addingCategory, setAddingCategory] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [deletingCategory, setDeletingCategory] = useState<Category | null>(null);

  const [addingRound, setAddingRound] = useState(false);
  const [editingRound, setEditingRound] = useState<Round | null>(null);
  const [deletingRound, setDeletingRound] = useState<Round | null>(null);

  useEffect(() => {
    fetchAll();
  }, [tournamentId]);

  const fetchAll = async () => {
    setLoading(true);
    const [catRes, roundRes, disabledRes] = await Promise.all([
      supabase
        .from('tournament_categories')
        .select('*')
        .eq('tournament_id', tournamentId)
        .order('sort_order')
        .order('created_at'),
      supabase
        .from('tournament_rounds')
        .select('*')
        .eq('tournament_id', tournamentId)
        .order('sort_order')
        .order('round_number'),
      supabase
        .from('system_settings')
        .select('value')
        .eq('key', `disabled_rounds_${tournamentId}`)
        .maybeSingle(),
    ]);
    if (!catRes.error) setCategories(catRes.data ?? []);
    if (!roundRes.error) setRounds(roundRes.data ?? []);
    if (disabledRes.data?.value) {
      const arr = disabledRes.data.value as number[];
      setDisabledRounds(new Set(Array.isArray(arr) ? arr : []));
    } else {
      setDisabledRounds(new Set());
    }
    setLoading(false);
  };

  const saveCategory = async (data: { name: string; description: string }) => {
    if (editingCategory) {
      const { error } = await supabase
        .from('tournament_categories')
        .update({ name: data.name, description: data.description || null })
        .eq('id', editingCategory.id);
      if (error) { toast.error('Failed to update category'); return; }
      toast.success('Category updated');
      setEditingCategory(null);
    } else {
      const { error } = await supabase
        .from('tournament_categories')
        .insert({ tournament_id: tournamentId, name: data.name, description: data.description || null, sort_order: categories.length });
      if (error) { toast.error('Failed to add category'); return; }
      toast.success('Category added');
      setAddingCategory(false);
    }
    fetchAll();
  };

  const deleteCategory = async () => {
    if (!deletingCategory) return;
    const { error } = await supabase.from('tournament_categories').delete().eq('id', deletingCategory.id);
    if (error) { toast.error('Failed to delete category'); return; }
    toast.success('Category deleted');
    setDeletingCategory(null);
    fetchAll();
  };

  const moveCategoryOrder = async (cat: Category, dir: -1 | 1) => {
    const sorted = [...categories].sort((a, b) => a.sort_order - b.sort_order);
    const idx = sorted.findIndex(c => c.id === cat.id);
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    const swap = sorted[swapIdx];
    await Promise.all([
      supabase.from('tournament_categories').update({ sort_order: swap.sort_order }).eq('id', cat.id),
      supabase.from('tournament_categories').update({ sort_order: cat.sort_order }).eq('id', swap.id),
    ]);
    fetchAll();
  };

  const saveRound = async (data: { round_number: number; name: string; date: string; notes: string }) => {
    const payload = {
      round_number: data.round_number,
      name: data.name,
      date: data.date || null,
      notes: data.notes || null,
    };
    if (editingRound) {
      const { error } = await supabase.from('tournament_rounds').update(payload).eq('id', editingRound.id);
      if (error) { toast.error('Failed to update round'); return; }
      toast.success('Round updated');
      setEditingRound(null);
    } else {
      const { error } = await supabase
        .from('tournament_rounds')
        .insert({ tournament_id: tournamentId, ...payload, sort_order: rounds.length });
      if (error) { toast.error('Failed to add round'); return; }
      toast.success('Round added');
      setAddingRound(false);
    }
    fetchAll();
  };

  const deleteRound = async () => {
    if (!deletingRound) return;
    await supabase.from('hole_scores').delete().eq('round_id', deletingRound.id);
    await supabase.from('scorer_assignments').delete().eq('round_id', deletingRound.id);
    await supabase.from('player_pairings').delete().eq('round_id', deletingRound.id);
    const { error } = await supabase.from('tournament_rounds').delete().eq('id', deletingRound.id);
    if (error) { toast.error('Failed to delete round'); return; }
    toast.success('Round deleted');
    setDeletingRound(null);
    fetchAll();
  };

  const moveRoundOrder = async (round: Round, dir: -1 | 1) => {
    const sorted = [...rounds].sort((a, b) => a.sort_order - b.sort_order);
    const idx = sorted.findIndex(r => r.id === round.id);
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    const swap = sorted[swapIdx];
    await Promise.all([
      supabase.from('tournament_rounds').update({ sort_order: swap.sort_order }).eq('id', round.id),
      supabase.from('tournament_rounds').update({ sort_order: round.sort_order }).eq('id', swap.id),
    ]);
    fetchAll();
  };

  const toggleRoundEnabled = async (round: Round) => {
    const roundNum = round.round_number;
    const newDisabled = new Set(disabledRounds);
    if (newDisabled.has(roundNum)) {
      newDisabled.delete(roundNum);
    } else {
      newDisabled.add(roundNum);
    }
    const arr = Array.from(newDisabled);
    const { error } = await supabase
      .from('system_settings')
      .upsert(
        { key: `disabled_rounds_${tournamentId}`, value: arr, category: 'tournament', is_public: true },
        { onConflict: 'key' }
      );
    if (error) {
      toast.error('Failed to update round');
      return;
    }
    setDisabledRounds(newDisabled);
    toast.success(newDisabled.has(roundNum) ? 'Round disabled' : 'Round enabled');
  };

  const nextRoundNumber = rounds.length > 0
    ? Math.max(...rounds.map(r => r.round_number)) + 1
    : 1;

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-golf-orange border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      {/* ── Categories ── */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="bg-golf-orange/10 p-2 rounded-lg">
              <TagIcon className="w-5 h-5 text-golf-orange" />
            </div>
            <div>
              <h3 className="font-semibold text-golf-gray">Categories</h3>
              <p className="text-xs text-gray-500">{categories.length} defined</p>
            </div>
          </div>
          {!addingCategory && !editingCategory && (
            <button
              onClick={() => setAddingCategory(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-golf-orange text-white rounded-lg hover:bg-golf-orange/90 transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
              Add
            </button>
          )}
        </div>

        <div className="space-y-2">
          <AnimatePresence>
            {addingCategory && (
              <motion.div
                key="add-cat-form"
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
              >
                <CategoryForm
                  onSave={saveCategory}
                  onCancel={() => setAddingCategory(false)}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {categories.length === 0 && !addingCategory && (
            <div className="text-center py-10 bg-gray-50 rounded-xl border border-dashed border-gray-200">
              <TagIcon className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-400">No categories yet</p>
              <button
                onClick={() => setAddingCategory(true)}
                className="mt-3 text-sm text-golf-orange hover:underline"
              >
                Add first category
              </button>
            </div>
          )}

          {categories.map((cat, idx) => (
            <motion.div
              key={cat.id}
              layout
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {editingCategory?.id === cat.id ? (
                <CategoryForm
                  initial={cat}
                  onSave={saveCategory}
                  onCancel={() => setEditingCategory(null)}
                />
              ) : (
                <div className="bg-white rounded-xl border border-gray-200 px-4 py-3 flex items-center gap-3 group">
                  <div className="flex flex-col gap-0.5 flex-shrink-0">
                    <button
                      onClick={() => moveCategoryOrder(cat, -1)}
                      disabled={idx === 0}
                      className="p-0.5 text-gray-300 hover:text-gray-500 disabled:opacity-20 transition-colors"
                    >
                      <ChevronUpIcon className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => moveCategoryOrder(cat, 1)}
                      disabled={idx === categories.length - 1}
                      className="p-0.5 text-gray-300 hover:text-gray-500 disabled:opacity-20 transition-colors"
                    >
                      <ChevronDownIcon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-golf-gray text-sm">{cat.name}</p>
                    {cat.description && (
                      <p className="text-xs text-gray-400 truncate">{cat.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => { setEditingCategory(cat); setAddingCategory(false); }}
                      className="p-1.5 rounded-lg text-gray-400 hover:text-golf-orange hover:bg-golf-orange/5 transition-colors"
                    >
                      <PencilIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setDeletingCategory(cat)}
                      className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>

      {/* ── Golf Rounds ── */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="bg-blue-500/10 p-2 rounded-lg">
              <CalendarDaysIcon className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <h3 className="font-semibold text-golf-gray">Golf Rounds</h3>
              <p className="text-xs text-gray-500">{rounds.length} defined</p>
            </div>
          </div>
          {!addingRound && !editingRound && (
            <button
              onClick={() => setAddingRound(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
              Add
            </button>
          )}
        </div>

        <div className="space-y-2">
          <AnimatePresence>
            {addingRound && (
              <motion.div
                key="add-round-form"
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
              >
                <RoundForm
                  nextRoundNumber={nextRoundNumber}
                  onSave={saveRound}
                  onCancel={() => setAddingRound(false)}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {rounds.length === 0 && !addingRound && (
            <div className="text-center py-10 bg-gray-50 rounded-xl border border-dashed border-gray-200">
              <CalendarDaysIcon className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-400">No rounds defined yet</p>
              <button
                onClick={() => setAddingRound(true)}
                className="mt-3 text-sm text-blue-500 hover:underline"
              >
                Add first round
              </button>
            </div>
          )}

          {rounds.map((round, idx) => (
            <motion.div
              key={round.id}
              layout
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {editingRound?.id === round.id ? (
                <RoundForm
                  initial={round}
                  nextRoundNumber={nextRoundNumber}
                  onSave={saveRound}
                  onCancel={() => setEditingRound(null)}
                />
              ) : (
                <div className={`bg-white rounded-xl border ${!disabledRounds.has(round.round_number) ? 'border-gray-200' : 'border-gray-200 opacity-60'} px-4 py-3 flex items-center gap-3 group`}>
                  <div className="flex flex-col gap-0.5 flex-shrink-0">
                    <button
                      onClick={() => moveRoundOrder(round, -1)}
                      disabled={idx === 0}
                      className="p-0.5 text-gray-300 hover:text-gray-500 disabled:opacity-20 transition-colors"
                    >
                      <ChevronUpIcon className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => moveRoundOrder(round, 1)}
                      disabled={idx === rounds.length - 1}
                      className="p-0.5 text-gray-300 hover:text-gray-500 disabled:opacity-20 transition-colors"
                    >
                      <ChevronDownIcon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-bold text-blue-600">{round.round_number}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-golf-gray text-sm">{round.name}</p>
                      {disabledRounds.has(round.round_number) && (
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">Disabled</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      {round.date && (
                        <span className="text-xs text-gray-400">
                          {new Date(round.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                      )}
                      {round.notes && (
                        <span className="text-xs text-gray-400 truncate">{round.notes}</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => toggleRoundEnabled(round)}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ${
                      !disabledRounds.has(round.round_number) ? 'bg-golf-green' : 'bg-gray-300'
                    }`}
                    title={!disabledRounds.has(round.round_number) ? 'Click to disable round (exclude from leaderboard)' : 'Click to enable round (include in leaderboard)'}
                  >
                    <span
                      className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow-sm transform transition-transform ${
                        !disabledRounds.has(round.round_number) ? 'translate-x-4.5' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => { setEditingRound(round); setAddingRound(false); }}
                      className="p-1.5 rounded-lg text-gray-400 hover:text-golf-orange hover:bg-golf-orange/5 transition-colors"
                    >
                      <PencilIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setDeletingRound(round)}
                      className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>

      <DeleteConfirmationModal
        isOpen={!!deletingCategory}
        onClose={() => setDeletingCategory(null)}
        onConfirm={deleteCategory}
        title="Delete Category"
        message={`Are you sure you want to delete the category "${deletingCategory?.name}"? This cannot be undone.`}
      />

      <DeleteConfirmationModal
        isOpen={!!deletingRound}
        onClose={() => setDeletingRound(null)}
        onConfirm={deleteRound}
        title="Delete Round"
        message={`Are you sure you want to delete "${deletingRound?.name}"? This cannot be undone.`}
      />
    </div>
  );
}
